"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { Pagination } from "@/components/ui/pagination";
import { PostVisual } from "@/components/sections/editorial/_shared/archive/parts";
import { ALTERNATIVE_CARDS } from "./data";

const CARD_BG = "color-mix(in srgb, var(--surface-1) 45%, var(--background))";
const CARD_BORDER = "color-mix(in srgb, var(--foreground) 8%, transparent)";
const PAGE_SIZE = 6;

export default function AlternativeCards() {
  const reduced = useReducedMotion() ?? false;
  const router = useRouter();
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(ALTERNATIVE_CARDS.length / PAGE_SIZE);
  const pageCards = ALTERNATIVE_CARDS.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handlePageChange(nextPage: number) {
    setPage(nextPage);
    document.getElementById("alternatives-grid-heading")?.scrollIntoView({ block: "start" });
  }

  return (
    <section
      aria-labelledby="alternatives-grid-heading"
      className="border-border border-b pt-[--spacing-section] pb-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mt-4 mb-12 max-w-xl">
          <h2
            id="alternatives-grid-heading"
            className="text-foreground mb-3 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            All Comparisons
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Every provider we&apos;ve compared Proxidize against, using their own published pricing
            and specs.
          </p>
        </div>

        <ul className="grid list-none grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pageCards.map((card, i) => (
            <motion.li
              key={card.href}
              initial={reduced ? false : { opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.4, delay: Math.min(i, 5) * 0.06 }}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => router.push(card.href)}
                onKeyDown={(e) => e.key === "Enter" && router.push(card.href)}
                className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl transition-all duration-300"
                style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  style={{
                    background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${card.accent}18, transparent 70%)`,
                  }}
                />

                <div
                  className="relative overflow-hidden"
                  style={{ aspectRatio: "300 / 157", borderBottom: `1px solid ${CARD_BORDER}` }}
                >
                  <PostVisual color={card.accent} seed={card.id} ns="alt" />
                </div>

                <div className="relative flex flex-1 flex-col p-5">
                  <span
                    className="mb-2 inline-block w-fit rounded-md px-2 py-0.5 text-[11px] font-semibold"
                    style={{ background: `${card.accent}18`, color: card.accent }}
                  >
                    vs {card.competitor}
                  </span>
                  <h3 className="text-foreground mb-2 text-base leading-snug font-semibold transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-muted-foreground flex-1 text-sm leading-relaxed">
                    {card.description}
                  </p>
                  <div className="mt-4 flex justify-end">
                    <Link
                      href={card.href}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-primary text-primary-foreground inline-flex items-center rounded-lg px-4 py-2 text-xs font-semibold transition-opacity hover:opacity-90"
                    >
                      Read the comparison →
                    </Link>
                  </div>
                </div>
              </div>
            </motion.li>
          ))}
        </ul>

        <Pagination current={page} total={totalPages} onChange={handlePageChange} />
      </div>
    </section>
  );
}
